import { createContext, useCallback, useContext, useEffect, useMemo, useRef, ReactNode } from 'react';
import { CompanyModel, UserMembershipTypeModel } from '@mesterplan/shared-types';
import type { ApiResult, CompanyDto, MesterplanApi, MyCompanyDto } from '@mesterplan/api-client';
import { useApiQuery, useMesterplanApi } from '@mesterplan/web-hooks';
import { useAuth } from '@mesterplan/auth-react';
import { useActiveOrgId, useOrgNotAvailableId, useSetActiveOrgId } from '@/contexts/NetClientsContext';
import { isSupplierPlan } from '@/contexts/activeOrg.helpers';
import { deriveCompanyFlags, isSupplierAccessRemoved, mapCompanyDto, isSlimReadMissing, mapMyCompany, sessionCompanyDto, toRelationship, toMembershipRole } from '@/contexts/companyMappers';

interface CompanyContextType {
  selectedCompany: CompanyModel | null;
  companies: CompanyModel[];
  isLoadingCompanies: boolean;
  isPartnerUser: boolean;
  isPartnerCompany: boolean;
  /** The active org is a Supplier-plan organisation: the supplier shell applies. */
  isSupplierOrg: boolean;
  /** The active org is a customer reached through a supplier access grant (an account manager inside a customer). */
  isSupplierSession: boolean;
  /** The supplier org to go back to from a supplier session (or the user's own supplier org). */
  supplierOrg: CompanyModel | null;
  /** Name of the active company from the switcher row — set even when the full record is not readable (supplier sessions). */
  activeCompanyName: string | null;
  /** True once the API refused a supplier session ("organization not available"): the grant was revoked. */
  supplierAccessRemoved: boolean;
  /** Leave a customer: make the supplier org active again. */
  leaveSupplierSession: () => void;
  /**
   * The current user's membership role in the active org (`owner|admin|user`),
   * or null when unknown. Sourced from the .NET membership list.
   */
  activeRole: UserMembershipTypeModel | null;
  setSelectedCompany: (company: CompanyModel | null) => void;
  refetchCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}

interface CompanyProviderProps {
  children: ReactNode;
}

/**
 * Companies come from the .NET membership list. The old Firebase sibling
 * (a `getDocsFromServer('COMPANIES')` scan filtered client-side by membership)
 * is gone: it only ran when `idpEnabled` was false, and there is no such build.
 * `idpEnabled` is `Boolean(VITE_IDP_ISSUER_URL)`, which is set in both
 * `.env.development` and the production build — and without it login is
 * disabled outright, so a non-IDP build has never been a working configuration.
 */
export function CompanyProvider({ children }: CompanyProviderProps) {
  return <NetCompanyProvider>{children}</NetCompanyProvider>;
}

function NetCompanyProvider({ children }: CompanyProviderProps) {
  const { status } = useAuth();
  // App.tsx mounts NetClientsProvider with the real MesterplanApi; the web-hooks
  // mirror is intentionally too thin (no users.myCompanies), so cast to the full
  // client at this boundary — same approach as App.tsx's netApi cast.
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const activeOrgId = useActiveOrgId();
  const setActiveOrgId = useSetActiveOrgId();

  const authenticated = status === 'authenticated';

  // The user's company memberships — powers the switcher dropdown.
  // The in-flight list is kept so the company read below can learn whether the active org is a
  // supplier session before it picks an endpoint (hooks' effects run in declaration order).
  const listRef = useRef<Promise<ApiResult<MyCompanyDto[]>> | null>(null);
  const companiesQuery = useApiQuery<MyCompanyDto[]>(
    () => {
      const request = (authenticated
        ? api.users.myCompanies()
        : Promise.resolve({
            ok: false as const,
            status: 0,
            error: { status: 0, detail: 'not-authenticated' },
          })) as Promise<ApiResult<MyCompanyDto[]>>;
      listRef.current = request;
      return request;
    },
    [authenticated],
  );

  // Full record for the active org — `selectedCompany` is derived strictly from
  // this so it always carries settings/profile, never a half-populated switcher
  // row. activeOrgId is the single source of truth for "which company".
  // The org the API refused with `org_not_available` on any request (access removed).
  const notAvailableOrgId = useOrgNotAvailableId();
  // Part of the query's deps: when the list (re)loads and shows the active org as a supplier row,
  // the company read re-runs through the slim endpoint instead of staying on an earlier failure.
  const rowIsSupplier =
    companiesQuery.data?.some((c) => c.id === activeOrgId && toRelationship(c.relationship) === 'supplier') ?? false;
  const companyQuery = useApiQuery<CompanyDto>(
    async (): Promise<ApiResult<CompanyDto>> => {
      if (!activeOrgId) {
        return { ok: false, status: 0, error: { status: 0, detail: 'no-active-org' } };
      }
      const list = listRef.current ? await listRef.current : null;
      const row = list?.ok ? list.data.find((c) => c.id === activeOrgId) : undefined;
      if (row && toRelationship(row.relationship) === 'supplier') {
        // A supplier cannot read GET /api/companies/{id} (no settings.view): use the slim read.
        const slim = await api.companies.supplierSessionCompany();
        if (slim.ok) return { ok: true, status: slim.status, data: sessionCompanyDto(row, slim.data) };
        // Older backend without the slim read (a route 404, no ProblemDetails code): the row's name
        // is all there is, and the module setting stays unknown. A 404 WITH a code is "company not
        // found": returned as the failure it is, and the list probe below decides if access is gone.
        if (isSlimReadMissing(slim.status, slim.error)) return { ok: true, status: 200, data: sessionCompanyDto(row, null) };
        return slim;
      }
      return api.companies.get(activeOrgId);
    },
    [activeOrgId, rowIsSupplier],
  );

  const companies = useMemo<CompanyModel[]>(
    () => (companiesQuery.status === 'success' && companiesQuery.data ? companiesQuery.data.map(mapMyCompany) : []),
    [companiesQuery.status, companiesQuery.data],
  );

  const selectedCompany = useMemo<CompanyModel | null>(
    () =>
      activeOrgId && companyQuery.status === 'success' && companyQuery.data
        ? mapCompanyDto(companyQuery.data)
        : null,
    [activeOrgId, companyQuery.status, companyQuery.data],
  );

  // Selecting a company just drives the active org; X-Active-Org then follows
  // and every .NET read re-scopes. Unifies the two former notions of active org.
  const setSelectedCompany = useCallback(
    (company: CompanyModel | null) => {
      setActiveOrgId(company ? company.id : null);
    },
    [setActiveOrgId],
  );

  const refetchCompanies = useCallback(async () => {
    companiesQuery.refetch();
    companyQuery.refetch();
  }, [companiesQuery, companyQuery]);

  const { isLoadingCompanies, isPartnerUser } = deriveCompanyFlags({
    authStatus: status,
    companiesStatus: companiesQuery.status,
    companyStatus: companyQuery.status,
    companiesCount: companies.length,
    hasActiveOrg: activeOrgId !== null,
  });
  const isPartnerCompany = selectedCompany?.plan === 'partner';

  // The active org's membership role lives on the switcher rows (MyCompanyDto),
  // not on the full company record — mapMyCompany drops it, so read it off the
  // raw query data. Powers isOwner/isAdmin in usePermissions under IDP.
  const activeRole = useMemo<UserMembershipTypeModel | null>(() => {
    if (!activeOrgId || companiesQuery.status !== 'success' || !companiesQuery.data) return null;
    const row = companiesQuery.data.find((c) => c.id === activeOrgId);
    return toMembershipRole(row?.role);
  }, [activeOrgId, companiesQuery.status, companiesQuery.data]);

  // Shell decisions come from the switcher row, which is known as soon as the list
  // has loaded — not from the full company record, which lags one request behind.
  const activeRow = useMemo<CompanyModel | null>(
    () => (activeOrgId ? companies.find((c) => c.id === activeOrgId) ?? null : null),
    [activeOrgId, companies],
  );
  const isSupplierSession = activeRow?.relationship === 'supplier';
  const isSupplierOrg = !isSupplierSession && (isSupplierPlan(activeRow?.plan) || isSupplierPlan(selectedCompany?.plan));
  const supplierOrg = useMemo<CompanyModel | null>(() => {
    const own = companies.find((c) => c.relationship !== 'supplier' && c.plan === 'supplier') ?? null;
    if (!activeRow || activeRow.relationship !== 'supplier') return own;
    return companies.find((c) => c.id === activeRow.supplierCompanyId) ?? own;
  }, [companies, activeRow]);
  // Access is "removed" only on an explicit `org_not_available`, or when the active supplier
  // row is gone from a refreshed list. A plain 403 is just a permission the supplier lacks.
  const supplierSessionOrgRef = useRef<string | null>(null);
  useEffect(() => {
    if (isSupplierSession) supplierSessionOrgRef.current = activeOrgId;
  }, [isSupplierSession, activeOrgId]);
  const probedOrgRef = useRef<string | null>(null);
  const permissionDenied =
    companyQuery.status === 'error' && (companyQuery.error?.status === 403 || companyQuery.error?.status === 404);
  useEffect(() => {
    // Ask the list again once: if the grant was revoked, the row is no longer there.
    if (isSupplierSession && permissionDenied && probedOrgRef.current !== activeOrgId) {
      probedOrgRef.current = activeOrgId;
      companiesQuery.refetch();
    }
  }, [isSupplierSession, permissionDenied, activeOrgId, companiesQuery]);
  const sessionRowGone =
    activeOrgId !== null &&
    supplierSessionOrgRef.current === activeOrgId &&
    companiesQuery.status === 'success' &&
    activeRow === null;
  const supplierAccessRemoved = isSupplierAccessRemoved({
    orgNotAvailable: activeOrgId !== null && notAvailableOrgId === activeOrgId,
    sessionRowGone,
  });
  const activeCompanyName = selectedCompany?.name ?? activeRow?.name ?? null;
  const leaveSupplierSession = useCallback(() => {
    setActiveOrgId(supplierOrg ? supplierOrg.id : null);
    // The grant may be gone: reload the switcher so the dead row disappears.
    companiesQuery.refetch();
  }, [setActiveOrgId, supplierOrg, companiesQuery]);

  const value: CompanyContextType = {
    selectedCompany,
    companies,
    isLoadingCompanies,
    isPartnerUser,
    isPartnerCompany,
    isSupplierOrg,
    isSupplierSession,
    supplierOrg,
    activeCompanyName,
    supplierAccessRemoved,
    leaveSupplierSession,
    activeRole,
    setSelectedCompany,
    refetchCompanies,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}
